import { useCallback, useState } from 'react';
import { pauseOrganization, unpauseOrganization } from '../../services/admin';
import type { PauseOrganizationResponse } from '../../types/admin';

export const PAUSE_MESSAGE_MAX_LENGTH = 500;

type PauseModalKind = 'pause' | 'unpause';

interface UseOrganizationPauseOptions {
  organizationId: string | undefined;
  /** Called with the API response so the caller can update local state. */
  onPauseStateChanged: (result: PauseOrganizationResponse) => void;
}

/**
 * Owns the pause/resume confirmation flow for one organization: which modal is
 * open, the optional custom message, in-flight state, and any server error.
 */
export const useOrganizationPause = ({
  organizationId,
  onPauseStateChanged,
}: UseOrganizationPauseOptions) => {
  const [modal, setModal] = useState<PauseModalKind | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Opens the pause confirm, pre-filled with any existing custom message. */
  const openPause = useCallback((existingMessage: string | null) => {
    setError(null);
    setMessage(existingMessage ?? '');
    setModal('pause');
  }, []);

  const openUnpause = useCallback(() => {
    setError(null);
    setModal('unpause');
  }, []);

  const close = useCallback(() => {
    setModal(null);
    setError(null);
  }, []);

  const run = useCallback(
    async (action: () => Promise<PauseOrganizationResponse>, fallbackError: string) => {
      setLoading(true);
      setError(null);

      try {
        const result = await action();
        onPauseStateChanged(result);
        setModal(null);
      } catch (err: unknown) {
        // Keep the modal open so the failure (e.g. "Cannot pause your own
        // organization") stays visible instead of failing silently.
        console.error(fallbackError, err);
        setError(err instanceof Error ? err.message : fallbackError);
      } finally {
        setLoading(false);
      }
    },
    [onPauseStateChanged]
  );

  const confirmPause = useCallback(async () => {
    if (!organizationId) return;

    const trimmedMessage = message.trim();
    await run(
      () => pauseOrganization(organizationId, trimmedMessage || null),
      'Failed to pause organization.'
    );
  }, [organizationId, message, run]);

  const confirmUnpause = useCallback(async () => {
    if (!organizationId) return;

    await run(() => unpauseOrganization(organizationId), 'Failed to resume organization.');
  }, [organizationId, run]);

  return {
    modal,
    message,
    setMessage,
    loading,
    error,
    openPause,
    openUnpause,
    close,
    confirmPause,
    confirmUnpause,
  };
};
